import { useState } from 'react';
import { captureError } from "@/lib/logger";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface CreateDriverSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess: (driver: any) => void;
}

export default function CreateDriverSheet({
    open,
    onOpenChange,
    onSuccess,
}: CreateDriverSheetProps) {
    const queryClient = useQueryClient();
    const [firstName, setFirstName] = useState('');
    const [middleName, setMiddleName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');

    const createDriverMutation = useMutation({
        mutationFn: async () => {
            // First create the driver record
            const { data: driverData, error: driverError } = await supabase
                .from('drivers')
                .insert({})
                .select('id')
                .single();

            if (driverError) throw driverError;

            // Then create the profile with driver_id reference and NULL user_id
            const { error: profileError } = await supabase
                .from('profiles')
                .insert({
                    user_id: null,
                    driver_id: driverData.id,
                    first_name: firstName.trim(),
                    middle_name: middleName.trim() || null,
                    last_name: lastName.trim(),
                    phone: phone.trim() || null,
                    address: address.trim() || null,
                });

            if (profileError) {
                // Rollback: delete the driver if profile creation fails
                await supabase.from('drivers').delete().eq('id', driverData.id);
                throw profileError;
            }

            return {
                id: driverData.id,
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                phone: phone.trim(),
            };
        },
        onSuccess: (newDriver) => {
            toast.success('Driver created successfully');
            queryClient.invalidateQueries({ queryKey: ['drivers-search'] });
            onSuccess(newDriver);
            onOpenChange(false);
            // Reset form
            setFirstName('');
            setMiddleName('');
            setLastName('');
            setPhone('');
            setAddress('');
        },
        onError: (error: any) => {
            captureError(error);
            if (error?.code === '23505' && error?.message?.includes('profiles_phone_key')) {
                toast.error('A driver with this phone number already exists');
            } else {
                toast.error('Failed to create driver');
            }
        },
    });

    const handleCreateDriver = () => {
        if (!firstName.trim() || !lastName.trim() || !phone.trim()) {
            toast.error('First name, last name and phone number are required');
            return;
        }
        createDriverMutation.mutate();
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="!inset-y-4 !right-4 !h-auto !w-[400px] !max-w-none rounded-2xl flex flex-col shadow-2xl border"
            >
                <SheetHeader>
                    <SheetTitle>Create New Driver</SheetTitle>
                </SheetHeader>

                <div className="flex-1 mt-6 space-y-4">
                    <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide" htmlFor="firstName">First Name *</Label>
                        <Input
                            id="firstName"
                            placeholder="Enter first name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide" htmlFor="middleName">Middle Name</Label>
                        <Input
                            id="middleName"
                            placeholder="Enter middle name"
                            value={middleName}
                            onChange={(e) => setMiddleName(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide" htmlFor="lastName">Last Name *</Label>
                        <Input
                            id="lastName"
                            placeholder="Enter last name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide" htmlFor="phone">Phone Number *</Label>
                        <Input
                            id="phone"
                            placeholder="Enter phone number"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide" htmlFor="address">Address</Label>
                        <Input
                            id="address"
                            placeholder="Enter address"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                        />
                    </div>
                </div>

                <div className="pt-4 border-t flex justify-end gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateDriver}
                        disabled={createDriverMutation.isPending || !firstName.trim() || !lastName.trim() || !phone.trim()}
                    >
                        {createDriverMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Create Driver
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
